import { admin } from "./axios";
import {
  ImageGeneratorFeedbackReason,
  ImageGeneratorLookView,
  ImageGeneratorPatternChoice,
  ImageGeneratorReview,
} from "./imageGenerator";

export type CollectionBuildGenerationStatus =
  | "assembled"
  | "generating"
  | "generated"
  | "failed";

export type CollectionBuildOutputType = "moodboard" | "look";

export type CollectionBuildRangePlanItem = {
  productType: string;
  quantity: number;
};

export type CollectionBuildSessionPayload = {
  season: string;
  seasonId?: string;
  genre: string;
  market: string;
  category: string;
  startingDirection?: string;
  skipReportThemes?: boolean;
  skipMoodboard?: boolean;
  macroThemeId?: string;
  colourStoryId?: string;
  colourStoryIds?: string[];
  patternTrendId?: string;
  /**
   * Collection Build micro-themes with pattern picks (catalog pattern-trend ids).
   * Active theme is listed first so hydrate can restore the switcher view.
   */
  patternTrendIds?: string[];
  /** Surface-report slide id when trend source is Surface Trend (IG parity). */
  surfaceId?: string;
  /** Multi-select surface-report slide ids (first matches surfaceId for back-compat). */
  surfaceIds?: string[];
  patternChoice?: ImageGeneratorPatternChoice;
  selectedAssetStems?: string[];
  paletteHexes?: string[];
  palettes?: Array<{
    id: string;
    label: string;
    colors: string[];
    swatches?: Array<{
      hex: string;
      name?: string;
      tier?: "major" | "minor";
      pantone?: string | null;
    }>;
  }>;
  garmentFormat?: string;
  embroideryType?: string;
  patternFormat?: string;
  denimMaterialId?: string;
  denimWashId?: string;
  additionalPrompt?: string;
  /** Catalog macro/capsule name or denim wash label for Recents. */
  themeLabel?: string;
  /** Full Recents label persisted on generation_sessions.session_title. */
  sessionTitle?: string;
  rangePlan: { items: CollectionBuildRangePlanItem[] };
  /**
   * Look Details draft (silhouette + sketch). Persisted so reopen/hydrate and
   * generate/looks can reuse fields chosen before moodboard.
   */
  looks?: CollectionBuildLookPayload[];
};

export type CollectionBuildLookPayload = {
  outputNumber?: number;
  styleIndex?: number;
  productType: string;
  influence?: string;
  styleType?: string;
  material?: string;
  materials?: string;
  sleeve?: string;
  neckline?: string;
  fit?: string;
  wash?: string;
  length?: string;
  /** Denim Look Details single-select catalog ids (applied on generate). */
  denimMaterialId?: string;
  denimWashId?: string;
  /** @deprecated Earlier multi-select drafts — hydrate uses first id. */
  denimMaterialIds?: string[];
  denimWashIds?: string[];
  /**
   * Print / Embroidery / Graphic selections in check order.
   * `rank` is primary | secondary | tertiary from that order.
   */
  garmentFormats?: Array<{
    format: string;
    rank?: "primary" | "secondary" | "tertiary";
    patternFormat?: string;
    embroideryType?: string;
  }>;
  /** Primary format mirror (BE fallback when garmentFormats omitted). */
  garmentFormat?: string;
  patternFormat?: string;
  embroideryType?: string;
  silhouetteId?: string;
  silhouetteName?: string;
  selectedSketchStems?: string[];
  sketchUploadName?: string;
  /** Uploaded silhouette image URL (look generation reference). */
  sketchUploadUrl?: string;
  /** Per-style Additional Input (optional). */
  additionalPrompt?: string;
  frontUrl?: string;
  backUrl?: string;
};

export type CollectionBuildHistoryRecord = {
  id: number;
  sessionId: number;
  createdAt: string;
  outputType: CollectionBuildOutputType;
  outputNumber: number | null;
  status: CollectionBuildGenerationStatus;
  season: string;
  prompt: string;
  model?: string | null;
  imageUrl: string | null;
  backImageUrl?: string | null;
  moodboardUrl?: string | null;
  moodboardAssetId?: number | null;
  sourceGenerationId?: number | null;
  /** Moodboard AI usage/cost after finalize (null while generating / for looks). */
  aiUsage?: {
    model?: string | null;
    size?: string | null;
    quality?: string | null;
    referenceCount?: number | null;
    openaiUsage?: Record<string, unknown> | null;
    cost?: {
      inputUsd: number;
      outputUsd: number;
      totalUsd: number;
    } | null;
  } | null;
  requestPayload?: Record<string, unknown>;
  review?: ImageGeneratorReview;
  references: {
    macroTheme: string | null;
    colourStory: string | null;
    colourSentiment?: string | null;
    patternTrend: string | null;
    patternSentiment?: string | null;
    palette: string[];
    assets: Array<{ stem: string; attached: boolean }>;
    silhouette: string | null;
  };
  details?: {
    genre: string;
    season: string;
    market: string;
    category: string;
    outputType: "Moodboard" | "Look";
    outputNumber?: number;
    productType?: string;
    influence?: string;
    styleType?: string;
    material?: string;
    sleeve?: string;
    neckline?: string;
    fit?: string;
    wash?: string;
    length?: string;
    startingDirection?: string;
    denimMaterialId?: string;
    denimWashId?: string;
    patternChoice?: ImageGeneratorPatternChoice;
    surfaceId?: string;
    macroTheme?: string;
    microTheme?: string;
    garmentFormat?: string;
    patternFormat?: string;
    paletteHexes: string[];
    palettes?: Array<{
      id: string;
      label: string;
      colors: string[];
      swatches?: Array<{
        hex: string;
        name?: string;
        tier?: "major" | "minor";
      }>;
    }>;
    preferences: Array<{ stem: string; url: string }>;
    sketchAssets?: Array<{ stem: string; url: string }>;
    rangePlanTotal?: number;
    rangePlanBreakdown?: CollectionBuildRangePlanItem[];
    skipMoodboard?: boolean;
    silhouetteName?: string;
    errorMessage?: string;
    /** Moodboard engine used for this output (`ai` | `svg_hybrid`). */
    engine?: "ai" | "svg_hybrid";
    /**
     * Structured moodboard SVG URL when engine=svg_hybrid.
     * Preview still uses imageUrl / moodboardUrl PNG.
     */
    svgUrl?: string;
    designSheet?: {
      status: "generating" | "generated" | "failed";
      slots: Array<{
        id: string;
        fileName: string;
        url: string;
        svgUrl?: string;
      }>;
      /** CB design sheet: `svg_hybrid` (canonical). `compose` is a legacy alias. */
      engine?: "svg_hybrid" | "ai" | "compose";
      errorMessage?: string;
      startedAt?: string;
      finishedAt?: string;
    };
  };
};

export type CollectionBuildSessionDetail = {
  sessionId: number;
  status: string;
  skipMoodboard: boolean;
  rangePlan: { items: CollectionBuildRangePlanItem[] } | null;
  inputSelections: CollectionBuildSessionPayload | Record<string, unknown>;
  outputs: CollectionBuildHistoryRecord[];
};

export type CollectionBuildSessionSummary = {
  sessionId: number;
  status: string;
  title: string;
  season: string | null;
  market: string | null;
  category: string | null;
  /** Client-enriched / API macro/capsule/denim-wash label for Recents. */
  theme?: string | null;
  skipMoodboard: boolean;
  rangePlan: { items: CollectionBuildRangePlanItem[] } | null;
  lookCount: number;
  moodboardCount: number;
  previewUrl: string | null;
  createdAt: string;
  updatedAt: string;
};

export type CollectionBuildSessionList = {
  items: CollectionBuildSessionSummary[];
  total: number;
  limit: number;
  offset: number;
};

export type CollectionBuildSilhouetteSlide = {
  id: string;
  name: string;
  text: string;
  sliderImageIndex: number | null;
  image: {
    available: boolean;
    url: string;
  };
  reportId: number;
  reportTitle: string;
  productType?: string | null;
};

export type CollectionBuildSilhouettesCatalog = {
  season: {
    id: string;
    label: string;
  };
  denim: boolean;
  productFiltered: boolean;
  silhouettes: CollectionBuildSilhouetteSlide[];
};

export type CollectionBuildReportPage = {
  id: string;
  name: string;
  url: string;
  sliderImageIndex: number | null;
};

/** Colour/micro item — prefer per-item slide URL over parent deck pages. */
export type CollectionBuildItemReportPages = {
  id: string;
  sliderImageUrl?: string | null;
  sliderImageIndex?: number | null;
  pages: CollectionBuildReportPage[];
};

export type CollectionBuildThemeReportPages = {
  id: string;
  pages: CollectionBuildReportPage[];
  colourStories: CollectionBuildItemReportPages[];
  patternTrends: CollectionBuildItemReportPages[];
};

export type CollectionBuildReportPagesCatalog = {
  season: {
    id: string;
    label: string;
  };
  macros: CollectionBuildThemeReportPages[];
  capsules: CollectionBuildThemeReportPages[];
};

export const getCollectionBuildErrorMessage = (
  error: unknown,
  fallback: string
) => {
  const message = (error as { response?: { data?: { message?: string } } })
    ?.response?.data?.message;
  return message || fallback;
};

export const getCollectionBuildSilhouettesCatalog = async (params: {
  season: string;
  seasonId?: string;
  market: string;
  category: string;
  startingDirection?: string;
  productTypes?: string[];
}) => {
  const response = await admin.get("collection-build/silhouettes-catalog", {
    params: {
      season: params.season,
      seasonId: params.seasonId || undefined,
      market: params.market,
      category: params.category,
      startingDirection: params.startingDirection || undefined,
      productTypes: params.productTypes?.length
        ? params.productTypes.join(",")
        : undefined,
    },
  });
  return response.data?.data as CollectionBuildSilhouettesCatalog;
};

export const getCollectionBuildReportPagesCatalog = async (params: {
  season: string;
  seasonId?: string;
  market: string;
  category: string;
}) => {
  const response = await admin.get("collection-build/report-pages-catalog", {
    params: {
      season: params.season,
      seasonId: params.seasonId || undefined,
      market: params.market,
      category: params.category,
    },
  });
  return response.data?.data as CollectionBuildReportPagesCatalog;
};

const ENQUEUE_TIMEOUT_MS = 45000;

export const createCollectionBuildSession = async (
  payload: CollectionBuildSessionPayload
) => {
  const response = await admin.post("collection-build/session", payload);
  const data = response.data?.data as {
    sessionId: number;
    inputId: number;
    status: string;
    skipMoodboard: boolean;
  };
  const sessionId = Number(data?.sessionId);
  if (!Number.isInteger(sessionId) || sessionId < 1) {
    throw new Error("Collection session was not created.");
  }
  return { ...data, sessionId };
};

export const updateCollectionBuildSession = async (
  sessionId: number,
  payload: CollectionBuildSessionPayload
) => {
  const response = await admin.patch(
    `collection-build/session/${sessionId}`,
    payload
  );
  const data = response.data?.data as {
    sessionId: number;
    inputId: number;
    status: string;
    skipMoodboard: boolean;
  };
  const id = Number(data?.sessionId ?? sessionId);
  if (!Number.isInteger(id) || id < 1) {
    throw new Error("Collection session was not updated.");
  }
  return { ...data, sessionId: id };
};

export const getCollectionBuildSession = async (sessionId: number) => {
  const response = await admin.get(`collection-build/session/${sessionId}`);
  return response.data?.data as CollectionBuildSessionDetail;
};

export const listCollectionBuildSessions = async (limit = 40, offset = 0) => {
  const response = await admin.get("collection-build/sessions", {
    params: { limit, offset },
  });
  return response.data?.data as CollectionBuildSessionList;
};

export const generateCollectionBuildMoodboard = async (
  payload: CollectionBuildSessionPayload & {
    sessionId: number;
    imageUrl?: string;
  }
) => {
  const response = await admin.post(
    "collection-build/generate/moodboard",
    payload,
    { timeout: ENQUEUE_TIMEOUT_MS }
  );
  return response.data?.data as CollectionBuildHistoryRecord;
};

export const generateCollectionBuildLooks = async (
  payload: CollectionBuildSessionPayload & {
    sessionId: number;
    looks: CollectionBuildLookPayload[];
    moodboardGenerationId?: number;
  }
) => {
  const response = await admin.post(
    "collection-build/generate/looks",
    payload,
    {
      timeout: ENQUEUE_TIMEOUT_MS,
    }
  );
  return response.data?.data as {
    items: CollectionBuildHistoryRecord[];
    sessionId: number;
  };
};

export const regenerateCollectionBuild = async (
  generationId: number,
  payload: { additionalPrompt?: string; views?: Array<"front" | "back"> } = {}
) => {
  const response = await admin.post(
    "collection-build/generate/regen",
    { generationId, ...payload },
    { timeout: ENQUEUE_TIMEOUT_MS }
  );
  return response.data?.data as CollectionBuildHistoryRecord;
};

export const setCollectionBuildLike = async (
  generationId: number,
  view: ImageGeneratorLookView,
  liked: boolean | null
) => {
  const response = await admin.post(
    `collection-build/history/${generationId}/like`,
    { view, liked }
  );
  return response.data?.data as CollectionBuildHistoryRecord;
};

export const submitCollectionBuildFeedback = async (
  generationId: number,
  payload: {
    view: ImageGeneratorLookView;
    reason: ImageGeneratorFeedbackReason;
    details?: string;
  }
) => {
  const response = await admin.post(
    `collection-build/history/${generationId}/feedback`,
    payload
  );
  return response.data?.data as CollectionBuildHistoryRecord;
};

export const approveCollectionBuild = async (generationId: number) => {
  const response = await admin.post(
    `collection-build/history/${generationId}/approve`
  );
  return response.data?.data as CollectionBuildHistoryRecord;
};

export const startCollectionBuildDesignSheet = async (
  generationId: number,
  payload: { imageUrl?: string; force?: boolean } = {}
) => {
  const response = await admin.post(
    `collection-build/history/${generationId}/design-sheet`,
    payload,
    { timeout: ENQUEUE_TIMEOUT_MS }
  );
  return response.data?.data as CollectionBuildHistoryRecord;
};
